/**
 * Card used on the public /contests listing.
 * Mobile-first, taps-through to /contests/{slug}.
 */
import { Link } from "react-router-dom";
import ContestStatusBadge from "./ContestStatusBadge";
import ContestCountdown from "./ContestCountdown";
import { contestCountdownTarget, deriveContestDisplayStatus } from "@/lib/contestStatus";
import { optimizeImage } from "@/lib/mediaUrls";

const rewardSummary = (contest) => {
  const r = (contest?.rewards || {})["1"] || {};
  const coins = Number(r.coins || 0);
  const promo = Number(r.promo_plays || 0);
  if (!coins && !promo) return null;
  const parts = [];
  if (coins) parts.push(`🪙 ${coins.toLocaleString()} coins`);
  if (promo && contest.entry_type === "track") parts.push(`⚡ ${promo.toLocaleString()} promo plays`);
  return parts.join(" · ");
};

export const ContestCard = ({ contest }) => {
  const s = deriveContestDisplayStatus(contest);
  const cd = contestCountdownTarget(contest);
  const summary = rewardSummary(contest);
  const banner = contest.banner_image;

  return (
    <Link
      to={`/contests/${contest.slug}`}
      className="group block rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-purple-500/50 hover:bg-white/[0.06] transition-all"
      data-testid={`contest-card-${contest.slug}`}
    >
      <div className="relative aspect-[16/9] bg-gradient-to-br from-purple-900/50 via-black to-amber-900/30 overflow-hidden">
        {banner ? (
          <img
            src={optimizeImage(banner, 'card')}
            alt={contest.title}
            className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
            data-testid={`contest-card-banner-${contest.slug}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl opacity-30">🏆</div>
        )}
        <div className="absolute top-3 left-3">
          <ContestStatusBadge contest={contest} />
        </div>
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-black/60 backdrop-blur-sm text-white border border-white/20">
          {contest.entry_type === "beat" ? "🎧 Beat" : "🎤 Track"}
        </div>
      </div>

      <div className="p-4 sm:p-5 space-y-2">
        <h3 className="text-lg sm:text-xl font-black text-white leading-tight group-hover:text-purple-200 transition-colors" data-testid={`contest-card-title-${contest.slug}`}>
          {contest.title}
        </h3>
        {contest.description && (
          <p className="text-sm text-gray-400 line-clamp-2">{contest.description}</p>
        )}
        {summary && (
          <p className="text-xs text-amber-300 font-black" data-testid={`contest-card-reward-${contest.slug}`}>
            Top reward: {summary}
          </p>
        )}
        {Number(contest?.entry_fee_coins || 0) > 0 && (
          <p
            className="text-[11px] font-black uppercase tracking-wider text-amber-200 inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5"
            data-testid={`contest-card-fee-${contest.slug}`}
          >
            🪙 Entry Fee: {Number(contest.entry_fee_coins).toLocaleString()} Coins
          </p>
        )}
        {cd && (
          <div className="pt-2 border-t border-white/5">
            <ContestCountdown targetIso={cd.target} label={cd.label} compact />
          </div>
        )}
      </div>
    </Link>
  );
};

export default ContestCard;
